import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { displayError } from '../../helpers';
import { getAllDeliveryChallans } from '../../services/DeliveryChallanServices';
import {
  TODAY_QTY_SUMMARY_COLUMNS,
  mapDeliveryChallanForTodayQty,
  buildTodayQtySummaryRows,
  formatTodayQtySummaryDisplayRows,
  buildTodayQtySummaryTotals,
  formatQty,
} from './todayQtySummaryUtils';

function TodayQtySummaryModal({
  open,
  onClose,
  cookies,
  plantId,
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => moment());

  const dateLabel = useMemo(
    () => selectedDate.format('DD-MM-YYYY'),
    [selectedDate],
  );

  const loadRowsForDate = useCallback(async (dateMoment) => {
    setLoading(true);
    const label = dateMoment.format('DD-MM-YYYY');
    const params = {
      from_date: label,
      upto_date: label,
    };
    if (plantId) {
      params.plant_ids = String(plantId);
    }
    try {
      const response = await getAllDeliveryChallans(cookies, params);
      const mapped = (response?.delivery_challan_list || []).map(mapDeliveryChallanForTodayQty);
      setRows(mapped);
    } catch (error) {
      displayError(error?.response?.data ?? error?.message, 'Failed to load delivery challans');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cookies, plantId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const today = moment();
    setSelectedDate(today);
    loadRowsForDate(today);
  }, [open, loadRowsForDate]);

  const summaryRows = useMemo(() => buildTodayQtySummaryRows(rows), [rows]);
  const displayRows = useMemo(
    () => formatTodayQtySummaryDisplayRows(summaryRows),
    [summaryRows],
  );
  const totals = useMemo(
    () => buildTodayQtySummaryTotals(summaryRows),
    [summaryRows],
  );
  const reportTitle = `Today's Qty Summary (${dateLabel})`;

  const shiftDate = (days) => {
    const nextDate = selectedDate.clone().add(days, 'days');
    setSelectedDate(nextDate);
    loadRowsForDate(nextDate);
  };

  const goToday = () => {
    const today = moment();
    setSelectedDate(today);
    loadRowsForDate(today);
  };

  if (!open) {
    return null;
  }

  return (
    <div className="modal today-qty-summary-modal" tabIndex="-1" style={{ display: 'block' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header today-qty-summary-modal-header">
            <h5 className="modal-title">{reportTitle}</h5>
            <button type="button" className="close text-white" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : displayRows.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No delivery challans found for {dateLabel}.
              </div>
            ) : (
              <div className="table-responsive no-column-freeze">
                <table className="table table-bordered today-qty-summary-table">
                  <thead>
                    <tr>
                      {TODAY_QTY_SUMMARY_COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          className={column.align === 'right' ? 'text-right' : undefined}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, index) => (
                      <tr key={`${row.customer}-${row.site}-${row.grade}-${row.marketing}-${index}`}>
                        {TODAY_QTY_SUMMARY_COLUMNS.map((column) => (
                          <td
                            key={column.key}
                            className={column.align === 'right' ? 'text-right' : undefined}
                          >
                            {row[column.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="today-qty-summary-total-row">
                      <td>Total</td>
                      <td />
                      <td />
                      <td className="text-right">{formatQty(totals.dcQty)}</td>
                      <td className="text-right">{formatQty(totals.acceptedQty)}</td>
                      <td className="text-right">{totals.loadCount}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer today-qty-summary-modal-footer">
            <div className="today-qty-summary-nav">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => shiftDate(-1)}
                disabled={loading}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={goToday}
                disabled={loading}
              >
                Today
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => shiftDate(1)}
                disabled={loading}
              >
                Next
              </button>
            </div>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TodayQtySummaryModal;
